import { ref } from 'vue';
import type { Router } from 'vue-router';
import { SecureStorage } from '@aparajita/capacitor-secure-storage';
import { api } from '@/lib/api';
import { isNativeApp, nativePlatform, sessionToken } from '@/lib/native';

/**
 * Notifications push (devis accepté, facture livrée, paiement reçu…) :
 * activées par l'utilisateur sur chaque téléphone (Préférences), jamais
 * demandées d'office. Le jeton est renvoyé à l'API à chaque ouverture, car
 * le système peut le changer.
 *
 * Android exige Firebase (`google-services.json`) : sans lui, l'inscription
 * ferait planter l'app, d'où `VITE_PUSH_ANDROID` (docs/notifications-push.md).
 */
const ENABLED_KEY = 'push-enabled';

export const pushAvailable =
  isNativeApp && (nativePlatform === 'ios' || import.meta.env.VITE_PUSH_ANDROID === 'true');
export const pushEnabled = ref(false);

/**
 * Renvoie le MODULE, pas le plugin : un plugin Capacitor est un proxy, et
 * `await` sur lui appelle `.then()` côté natif → « then() is not implemented ».
 */
async function plugin() {
  return import('@capacitor/push-notifications');
}

/** Demande un jeton au système ; il arrive par l'évènement `registration`. */
async function register(): Promise<boolean> {
  const { PushNotifications } = await plugin();
  let { receive } = await PushNotifications.checkPermissions();
  if (receive === 'prompt' || receive === 'prompt-with-rationale') receive = (await PushNotifications.requestPermissions()).receive;
  if (receive !== 'granted') return false;
  await PushNotifications.register();
  return true;
}

export async function setPushEnabled(enabled: boolean): Promise<boolean> {
  if (enabled && !(await register())) return false;
  if (!enabled) {
    await api.unregisterPushDevice().catch(() => undefined);
    await (await plugin()).PushNotifications.unregister().catch(() => undefined);
  }
  pushEnabled.value = enabled;
  await (enabled ? SecureStorage.set(ENABLED_KEY, '1') : SecureStorage.remove(ENABLED_KEY));
  return true;
}

export async function setupPush(router: Router): Promise<void> {
  if (!pushAvailable) return;
  const { PushNotifications } = await plugin();
  // Android : canal nommé (réglable par l'utilisateur dans les paramètres du
  // téléphone) au lieu du canal « Divers » par défaut. Idempotent.
  if (nativePlatform === 'android') {
    await PushNotifications.createChannel({
      id: 'novofa',
      name: 'Devis et factures',
      description: 'Devis acceptés ou refusés, factures livrées, refusées ou payées',
      importance: 4,
    }).catch(() => undefined);
  }

  await PushNotifications.addListener('registration', ({ value }) => {
    if (sessionToken()) {
      void api.registerPushDevice({ platform: nativePlatform as 'ios' | 'android', token: value }).catch(() => undefined);
    }
  });
  await PushNotifications.addListener('registrationError', () => {
    pushEnabled.value = false;
  });
  // Toucher la notification ouvre la page concernée (devis, facture).
  await PushNotifications.addListener('pushNotificationActionPerformed', ({ notification }) => {
    const path = (notification.data as { path?: unknown } | undefined)?.path;
    if (typeof path === 'string' && path.startsWith('/')) void router.push(path);
  });

  pushEnabled.value = ((await SecureStorage.get(ENABLED_KEY).catch(() => null)) as string | null) === '1';
  // Réinscription à chaque ouverture, connecté seulement, sans redemander l'autorisation.
  if (pushEnabled.value && sessionToken() && (await PushNotifications.checkPermissions()).receive === 'granted') {
    await PushNotifications.register();
  }
}

/** Après connexion : réabonne ce téléphone si l'utilisateur l'avait activé. */
export async function resumePushAfterLogin(): Promise<void> {
  if (pushAvailable && pushEnabled.value) await register().catch(() => undefined);
}
